import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/slp/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 max-w-md w-full text-center">
          <p className="text-[#595959] mb-4">Invalid reset link. Please request a new one.</p>
          <Link to="/forgot-password" className="text-[#2C4C3B] font-medium hover:underline text-sm">Request New Reset Link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#2C4C3B] rounded-2xl mb-4">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>FrameEd</h1>
          <p className="text-[#595959]">Referrals</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8">
          {success ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-2xl bg-[#2C4C3B]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-[#2C4C3B]" />
              </div>
              <h2 className="text-xl font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Password Reset</h2>
              <p className="text-sm text-[#595959] mb-6">Your password has been updated. You can now log in.</p>
              <Link to="/slp/login" className="inline-block bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium rounded-xl px-6 py-3 transition-colors text-sm">
                Go to Login
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-[#1C1C1C] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Set New Password</h2>
              <p className="text-sm text-[#595959] mb-6">Enter your new password below.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
                    <Lock className="w-4 h-4 inline mr-2 text-[#595959]" />New Password
                  </label>
                  <input
                    type="password"
                    required
                    data-testid="reset-new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    required
                    data-testid="reset-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-password-submit"
                  className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium rounded-xl px-6 py-3 transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959] disabled:cursor-default"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
